import axios from "axios";
import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { Search, UserRoundSearch, X } from "lucide-react";
import { useNotify } from "@/contexts/NotifyContext";
import { useAuth } from "@/contexts/AuthContext";
import { queryNames, queryUsers } from "@/services/DirectoryService";

export interface SearchOption {
	Name: string;
	Graduation: number;
	Email: string;
	AlumniId: number;
	UserId?:number // used for Roles Editor
}

function highlightMatch(label: string, query: string) {
	if (!query.trim()) return <>{label}</>;
	const idx = label.toLowerCase().indexOf(query.toLowerCase());
	if (idx === -1) return <>{label}</>;
	return (
		<>
			{label.slice(0, idx)}
			<span className="text-teal-700 font-semibold">
				{label.slice(idx, idx + query.length)}
			</span>
			{label.slice(idx + query.length)}
		</>
	);
}

export function AlumniSearch({onSelected, onChangeName,  userOnly = false}: {
	onSelected: (option: SearchOption | null) => void;
	onChangeName?: (value: string) => void;
	userOnly?: boolean
}) {
	const { setToken } = useAuth();
	const { setNotify } = useNotify();
	const [query, setQuery] = useState("");
	const [resultCount, setResultCount] = useState<number>(0)
	const [results, setResults] = useState<SearchOption[]|null>(null)
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const requestIdRef = useRef(0);

	useEffect(() => {
		if (!query.trim()) {
			setResults(null);
			setResultCount(0);
			return;
		}

		const currentRequestId = ++requestIdRef.current;
		setResults(null);
		setResultCount(0);

		const timeoutId = window.setTimeout(async () => {
			async function fetchUsers() {
				setNotify(null)
				setToken(axios);
				try {
					const response = userOnly ? await queryUsers(query) : await queryNames(query, 49);
					if (currentRequestId !== requestIdRef.current) return;
					console.log(response);
					setResultCount(response.count);
					setResults(response.rows);
				} catch (error) {
					if (currentRequestId !== requestIdRef.current) return;
					setNotify({ title: "Alumni Filter Failed", error, severity: 'error' });
				}
			}
			await fetchUsers();
		}, 250);

		return () => window.clearTimeout(timeoutId);
	}, [query, userOnly, setNotify, setToken]);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		setActiveIndex(-1);
	}, [query]);

	useEffect(() => {
		if (activeIndex >= 0 && listRef.current) {
			const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
			el?.scrollIntoView({ block: "nearest" });
		}
	}, [activeIndex]);

	const listScrollClass = results && results.length > 5 ? "overflow-y-auto" : "overflow-y-hidden";

	function selectOption(option: SearchOption) {
		onSelected(option);
		setQuery(option.Name);
		setIsOpen(false);
		setActiveIndex(-1);
		inputRef.current?.blur();
	}

	function clearInput() {
		setQuery("");
		onSelected(null);
		setIsOpen(false);
		inputRef.current?.focus();
	}

	function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
		if (!isOpen || (results && results.length === 0)) {
			if (e.key === "ArrowDown" && results && results.length > 0) setIsOpen(true);
			return;
		}
		if (e.key === "Escape") {
			setIsOpen(false);
			setActiveIndex(-1);
		} else if (!results) {

		} else if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIndex((prev) => (prev + 1) % results.length);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (activeIndex >= 0) selectOption(results[activeIndex]);
		}
	}

	return (
		<div ref={containerRef} className="relative">
			<div className="relative bg-white">
				<UserRoundSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
				<input
					id="name-search"
					ref={inputRef}
					type="text"
					role="combobox"
					aria-expanded={isOpen}
					aria-controls="search-listbox"
					aria-autocomplete="list"
					autoComplete="off"
					placeholder="input nama untuk mencari alumni"
					value={query}
					onChange={(e) => {
						setQuery(e.target.value);
						if (onChangeName) onChangeName(e.target.value)
						onSelected(null);
						setIsOpen(true);
					}}
					onFocus={() => query.trim() && setIsOpen(true)}
					onKeyDown={handleKeyDown}
					className="w-full rounded-lg border border-(--color-border) py-2.5 pl-9 pr-9 text-sm placeholder:text-slate-400 outline-none transition focus:ring-2 focus:ring-(--color-ring)"
				/>
				{query && (
					<button
						type="button"
						onClick={clearInput}
						aria-label="Clear input"
						className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
					>
						<X className="h-4 w-4" />
					</button>
				)}
			</div>

			{isOpen && query.trim() !== "" && (
				<ul
					id="search-listbox"
					ref={listRef}
					role="listbox"
					className={`absolute z-10 mt-0 max-h-64 w-full overflow-x-hidden rounded-lg border py-1 shadow-lg ${listScrollClass}`}
				>
					{results && (
						<li className="sticky top-0 z-10 border-b bg-slate-50 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
							{resultCount} result{resultCount === 1 ? "" : "s"} found
						</li>
					)}
					{results && results.length === 0 ? (
						<li className="px-3 py-2.5 text-sm bg-white text-slate-500">No results for "{query}"</li>
					) : (
						results && results.map((option, i) => (
							<li
								key={i}
								role="option"
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => selectOption(option)}
								onMouseEnter={() => setActiveIndex(i)}
								className={`flex cursor-pointer items-center justify-between px-3 py-1 text-sm ${i === activeIndex ? "bg-teal-50" : "bg-white"}`}
							>
								<div className="flex flex-col">
									<span className="text-slate-900">{highlightMatch(option.Name, query)}</span>
									{option.Email && (<span className="text-xs text-slate-400">{option.Email}</span>)}
									{option.Graduation && (<span className="text-xs text-slate-400 font-semibold">{option.Graduation}</span>)}
								</div>
							</li>
						))
					)}
				</ul>
			)}
		</div>
	);
}
